export function isPrimitive(v: unknown) {
  return (typeof v !== "object" && typeof v !== "function") || v === null;
}

export function deepMerge<T extends object>(base: T, custom: T): T {
  const output = { ...base };

  for (const key in custom) {
    if (
      custom[key] &&
      typeof custom[key] === "object" &&
      !Array.isArray(custom[key]) &&
      base[key] &&
      typeof base[key] === "object"
    ) {
      output[key] = deepMerge(base[key], custom[key]);
    } else {
      output[key] = custom[key];
    }
  }

  return output;
}

export function isDeepEqual(a: any, b: any) {
  // Strict equality for primitives and same object reference
  if (a === b) {
    return true;
  }

  /*
   * Handle null, undefined, and different types
   */
  if (
    a == null ||
    b == null ||
    typeof a !== "object" ||
    typeof b !== "object"
  ) {
    return false;
  }

  /*
   * Compare Arrays
   */
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) {
        return false;
      }
    }

    return true;
  }

  /*
   * Compare Sets
   */
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) {
      return false;
    }

    // Fast path: All values are primitives
    let allValuesArePrimitive = true;

    for (const value of a) {
      if (!isPrimitive(value)) {
        allValuesArePrimitive = false;
        break;
      }
    }

    if (allValuesArePrimitive) {
      for (const value of a) {
        if (!b.has(value)) {
          return false;
        }
      }

      return true;
    }

    // Fallback: Deep match each element
    const remaining = new Set(b);

    outer: for (const valueOfA of a) {
      for (const valueOfB of remaining) {
        if (isDeepEqual(valueOfA, valueOfB)) {
          remaining.delete(valueOfB);
          continue outer;
        }
      }

      return false;
    }

    return true;
  }

  /*
   * Compare Objects
   */
  if (typeof a === "object" && typeof b === "object") {
    const keysOfA = Object.keys(a);
    const keysOfB = Object.keys(b);

    if (keysOfA.length !== keysOfB.length) {
      return false;
    }

    for (const key of keysOfA) {
      if (!keysOfB.includes(key) || !isDeepEqual(a[key], b[key])) {
        return false;
      }
    }

    return true;
  }

  return false;
}
